import * as React from "react";
import { StatusIndicator } from "@amzn/awsui-components-react";
import { AppURLFactory } from "../../factory/app-url-factory";
import { buildHighlightParam, buildContextParam } from "../../utils/highlight-utils";
import "../../styles/component/text-highlight-share.scss";

const CONTEXT_LENGTH = 20;

function getEnclosingContainerId(node: Node): string | null {
    let el = node.nodeType === Node.ELEMENT_NODE ? node as HTMLElement : node.parentElement;
    if (!el) return null;
    const containerSection = el.closest(".custom-container");
    if (!containerSection) return null;
    const anchor = containerSection.querySelector("a[id]");
    if (anchor) {
        return anchor.getAttribute("id");
    }
    return null;
}

function getSurroundingContext(containerId: string, range: Range): { before: string; after: string } {
    const container = document.getElementById(containerId);
    if (!container) return { before: "", after: "" };

    const beforeRange = document.createRange();
    beforeRange.setStart(container, 0);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    const beforeText = (beforeRange.toString() || "").replace(/\s+/g, " ");
    const before = beforeText.substring(Math.max(0, beforeText.length - CONTEXT_LENGTH)).trim();

    const afterRange = document.createRange();
    afterRange.setStart(range.endContainer, range.endOffset);
    afterRange.setEnd(container, container.childNodes.length);
    const afterText = (afterRange.toString() || "").replace(/\s+/g, " ");
    const after = afterText.substring(0, CONTEXT_LENGTH).trim();

    return { before, after };
}

export const TextHighlightShare = () => {
    const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number } | null>(null);
    const [selectedText, setSelectedText] = React.useState<string>("");
    const [containerId, setContainerId] = React.useState<string | null>(null);
    const [copyStatus, setCopyStatus] = React.useState<"idle" | "success" | "error">("idle");
    const tooltipRef = React.useRef<HTMLDivElement>(null);
    const selectionRangeRef = React.useRef<Range | null>(null);

    const handleMouseUp = React.useCallback(() => {
        setTimeout(() => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || !selection.rangeCount) {
                setTooltipPos(null);
                setSelectedText("");
                return;
            }

            const text = selection.toString().trim();
            if (text.length < 3) {
                setTooltipPos(null);
                setSelectedText("");
                return;
            }

            const range = selection.getRangeAt(0);
            const enclosingContainer = getEnclosingContainerId(range.startContainer);
            if (!enclosingContainer) {
                setTooltipPos(null);
                setSelectedText("");
                return;
            }

            const endContainer = getEnclosingContainerId(range.endContainer);
            if (endContainer !== enclosingContainer) {
                setTooltipPos(null);
                setSelectedText("");
                return;
            }

            const rect = range.getBoundingClientRect();
            setTooltipPos({
                top: rect.top + window.scrollY - 40,
                left: rect.left + window.scrollX + (rect.width / 2)
            });
            selectionRangeRef.current = range.cloneRange();
            setSelectedText(text);
            setContainerId(enclosingContainer);
            setCopyStatus("idle");
        }, 10);
    }, []);

    const handleMouseDown = React.useCallback((e: MouseEvent) => {
        if (tooltipRef.current && tooltipRef.current.contains(e.target as Node)) {
            return;
        }
        setTooltipPos(null);
        setSelectedText("");
        setCopyStatus("idle");
    }, []);

    React.useEffect(() => {
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("mousedown", handleMouseDown);
        return () => {
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("mousedown", handleMouseDown);
        };
    }, [handleMouseUp, handleMouseDown]);

    const handleCopyLink = async () => {
        if (!containerId || !selectedText || !selectionRangeRef.current) return;

        const highlightParam = buildHighlightParam(selectedText);
        const { before, after } = getSurroundingContext(containerId, selectionRangeRef.current);
        const params = new Map<string, string>([["highlight", highlightParam]]);
        if (before || after) {
            params.set("highlightContext", buildContextParam(before, after));
        }
        const url = window.location.origin + AppURLFactory.getContainer(containerId, {
            params
        });

        try {
            await navigator.clipboard.writeText(url);
            setCopyStatus("success");
            setTimeout(() => {
                setTooltipPos(null);
                setSelectedText("");
                setCopyStatus("idle");
            }, 1500);
        } catch {
            setCopyStatus("error");
        }
    };

    if (!tooltipPos || !selectedText) return null;

    return (
        <div
            ref={tooltipRef}
            className="text-highlight-share-tooltip"
            style={{
                top: `${tooltipPos.top}px`,
                left: `${tooltipPos.left}px`,
            }}
        >
            {copyStatus === "idle" && (
                <button
                    className="text-highlight-share-button"
                    onClick={handleCopyLink}
                    title="Copy link to highlighted text"
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M6.354 5.5H4a3 3 0 0 0 0 6h3a3 3 0 0 0 2.83-4H9c-.086 0-.17.01-.25.031A2 2 0 0 1 7 10H4a2 2 0 1 1 0-4h1.535c.218-.376.495-.714.82-1z"/>
                        <path d="M9 5.5a3 3 0 0 0-2.83 4h1.098A2 2 0 0 1 9 6h3a2 2 0 1 1 0 4h-1.535a4.02 4.02 0 0 1-.82 1H12a3 3 0 1 0 0-6H9z"/>
                    </svg>
                    <span>Share highlight</span>
                </button>
            )}
            {copyStatus === "success" && (
                <StatusIndicator type="success">Link Copied</StatusIndicator>
            )}
            {copyStatus === "error" && (
                <StatusIndicator type="error">Copy Failed</StatusIndicator>
            )}
        </div>
    );
};
